import { ReactElement } from 'react'

/**
 * Parses custom markdown syntax and returns formatted React elements
 *
 * Supported syntax:
 * - *text* → italic
 * - **text** → bold
 * - __text__ → underlined
 * - [blue]text[/blue] → blue bold
 * - [red]text[/red] → red bold
 * - [green]text[/green] → green bold
 * - [text](url) → hyperlink
 * - \n or \r\n → line break
 */
export const parseMarkdown = (text: string): ReactElement => {
  if (!text) {
    return <span>-</span>
  }

  const parts: ReactElement[] = []
  let remainingText = text
  let key = 0

  while (remainingText.length > 0) {
    // Check for line breaks (carriage return + line feed or just line feed)
    if (remainingText.startsWith('\r\n')) {
      parts.push(<br key={key++} />)
      remainingText = remainingText.slice(2)
      continue
    }
    if (remainingText.startsWith('\n')) {
      parts.push(<br key={key++} />)
      remainingText = remainingText.slice(1)
      continue
    }

    // Check for [text](url) links
    const linkMatch = remainingText.match(/^\[([^\]]+)\]\(([^)]+)\)/)
    if (linkMatch) {
      parts.push(
        <a key={key++} href={linkMatch[2]} target="_blank" rel="noopener noreferrer">
          {linkMatch[1]}
        </a>
      )
      remainingText = remainingText.slice(linkMatch[0].length)
      continue
    }

    // Check for [blue]text[/blue]
    const blueMatch = remainingText.match(/^\[blue\]([^[]+)\[\/blue\]/)
    if (blueMatch) {
      parts.push(
        <span key={key++} style={{ color: '#007bff', fontWeight: 'bold' }}>
          {blueMatch[1]}
        </span>
      )
      remainingText = remainingText.slice(blueMatch[0].length)
      continue
    }

    // Check for [red]text[/red]
    const redMatch = remainingText.match(/^\[red\]([^[]+)\[\/red\]/)
    if (redMatch) {
      parts.push(
        <span key={key++} style={{ color: '#dc3545', fontWeight: 'bold' }}>
          {redMatch[1]}
        </span>
      )
      remainingText = remainingText.slice(redMatch[0].length)
      continue
    }

    // Check for [green]text[/green]
    const greenMatch = remainingText.match(/^\[green\]([^[]+)\[\/green\]/)
    if (greenMatch) {
      parts.push(
        <span key={key++} style={{ color: '#28a745', fontWeight: 'bold' }}>
          {greenMatch[1]}
        </span>
      )
      remainingText = remainingText.slice(greenMatch[0].length)
      continue
    }

    // Check for **text** bold
    const boldMatch = remainingText.match(/^\*\*([^*]+)\*\*/)
    if (boldMatch) {
      parts.push(<strong key={key++}>{boldMatch[1]}</strong>)
      remainingText = remainingText.slice(boldMatch[0].length)
      continue
    }

    // Check for *text* italic
    const italicMatch = remainingText.match(/^\*([^*]+)\*/)
    if (italicMatch) {
      parts.push(<em key={key++}>{italicMatch[1]}</em>)
      remainingText = remainingText.slice(italicMatch[0].length)
      continue
    }

    // Check for __text__ underlined
    const underlineMatch = remainingText.match(/^__([^_]+)__/)
    if (underlineMatch) {
      parts.push(
        <span key={key++} style={{ textDecoration: 'underline' }}>
          {underlineMatch[1]}
        </span>
      )
      remainingText = remainingText.slice(underlineMatch[0].length)
      continue
    }

    // No match found, add the next character as plain text
    parts.push(<span key={key++}>{remainingText[0]}</span>)
    remainingText = remainingText.slice(1)
  }

  return <>{parts}</>
}
